import Phaser from 'phaser'
import SceneKeys from '../consts/SceneKeys'
import { leaderboardService, HighScoreEntry } from '../services/LeaderboardService'

export default class HighScores extends Phaser.Scene {
  private highScores: HighScoreEntry[] = []
  private isLoading = true

  constructor() {
    super(SceneKeys.HighScores)
    console.log('🎮 HighScores scene constructed')
  }

  async init(data: { score: number }) {
    console.log('🎮 HighScores.init called with score:', data.score)
    this.isLoading = true

    // Show loading message
    this.showLoadingMessage()

    try {
      // Check if score qualifies for top 5 leaderboard
      const qualifies = await leaderboardService.isHighScore(data.score, 5)

      if (qualifies) {
        // Score qualifies! Show name input
        this.children.removeAll()
        this.showNameInput(data.score)
      } else {
        // Load and display top 5 leaderboard
        this.highScores = await leaderboardService.getHighScores(5)
        this.children.removeAll()
        this.createLeaderboard()
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      // On error, still show name input so player can try to submit
      this.children.removeAll()
      this.showNameInput(data.score)
    }

    this.isLoading = false
  }

  private showLoadingMessage() {
    const { width, height } = this.scale
    const centerX = width * 0.5
    const centerY = height * 0.5

    // Dark background
    const overlay = this.add.rectangle(0, 0, width, height, 0x0a0a1a, 1)
    overlay.setOrigin(0, 0)

    // Loading text
    const loadingText = this.add
      .text(centerX, centerY, 'Loading Leaderboard...', {
        fontSize: '32px',
        color: '#FFD700',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Animate loading text
    this.tweens.add({
      targets: loadingText,
      alpha: 0.5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })
  }

  private showNameInput(score: number) {
    console.log('📝 showNameInput called with score:', score)
    const { width, height } = this.scale
    const centerX = width * 0.5
    const centerY = height * 0.5

    // Dark overlay
    const overlay = this.add.rectangle(0, 0, width, height, 0x000000, 0.9)
    overlay.setOrigin(0, 0)

    // Congratulations text
    this.add
      .text(centerX, centerY - 150, '🎉 NEW HIGH SCORE! 🎉', {
        fontSize: '36px',
        color: '#FFD700',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)

    // Score display
    this.add
      .text(centerX, centerY - 90, `Score: ${score}`, {
        fontSize: '32px',
        color: '#FFFFFF',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Input prompt
    this.add
      .text(centerX, centerY - 30, 'Enter Your Name:', {
        fontSize: '24px',
        color: '#F8E71C',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)

    // Get the canvas position on the page
    const canvas = this.game.canvas
    const canvasRect = canvas.getBoundingClientRect()

    // Calculate the scale factor between canvas size and game size
    const scaleX = canvasRect.width / width
    const scaleY = canvasRect.height / height

    // Create HTML input element
    const inputElement = document.createElement('input')
    inputElement.type = 'text'
    inputElement.maxLength = 15
    inputElement.placeholder = 'Your Name'
    inputElement.style.position = 'absolute'

    // Position relative to the canvas with proper scaling
    const inputWidth = 300
    const inputHeight = 50
    const inputX = canvasRect.left + centerX * scaleX - inputWidth / 2
    const inputY = canvasRect.top + (centerY + 10) * scaleY

    inputElement.style.left = `${inputX}px`
    inputElement.style.top = `${inputY}px`

    inputElement.style.width = `${inputWidth}px`
    inputElement.style.height = `${inputHeight}px`
    inputElement.style.fontSize = '24px'
    inputElement.style.textAlign = 'center'
    inputElement.style.border = '3px solid #FFD700'
    inputElement.style.borderRadius = '10px'
    inputElement.style.backgroundColor = '#1a1a1a'
    inputElement.style.color = '#FFFFFF'
    inputElement.style.outline = 'none'
    inputElement.style.padding = '10px'
    inputElement.style.fontWeight = 'bold'
    inputElement.style.zIndex = '1000'

    document.body.appendChild(inputElement)
    inputElement.focus()

    // Disable Phaser's keyboard input while typing to prevent 'D' from triggering flag throw
    this.input.keyboard!.enabled = false

    // Re-enable when input loses focus
    inputElement.addEventListener('blur', () => {
      this.input.keyboard!.enabled = true
    })

    // Submit button
    const submitButton = this.add
      .text(centerX, centerY + 100, '[ SUBMIT ]', {
        fontSize: '28px',
        color: '#00FF00',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })

    // Hover effects
    submitButton.on('pointerover', () => {
      submitButton.setScale(1.1)
      submitButton.setColor('#00FFFF')
    })
    submitButton.on('pointerout', () => {
      submitButton.setScale(1)
      submitButton.setColor('#00FF00')
    })

    // Submit handler
    const submitScore = async () => {
      console.log('🔵 submitScore function called!')
      const name = inputElement.value.trim()
      console.log('🔵 Name entered:', name)
      console.log('🔵 Score to submit:', score)

      if (!name) {
        console.log('⚠️ No name entered, skipping submission')
        // If no name is entered, do not save the score
        this.input.keyboard!.enabled = true
        document.body.removeChild(inputElement)
        this.children.removeAll()
        await this.loadAndShowLeaderboard()
        return
      }

      // Disable button and input during submission
      console.log('🔵 Disabling button and input...')
      submitButton.disableInteractive()
      inputElement.disabled = true
      submitButton.setText('[ SUBMITTING... ]')

      try {
        console.log('🔵 Calling leaderboardService.submitScore...')
        // Submit score to Firebase
        await leaderboardService.submitScore(name, score)
        console.log('🔵 submitScore returned successfully')

        // Re-enable keyboard and remove input element
        this.input.keyboard!.enabled = true
        document.body.removeChild(inputElement)

        // Clear scene and show leaderboard
        this.children.removeAll()
        await this.loadAndShowLeaderboard()
      } catch (error) {
        console.error('Failed to submit score:', error)

        // Re-enable on error
        submitButton.setInteractive()
        inputElement.disabled = false
        submitButton.setText('[ SUBMIT - RETRY ]')
        submitButton.setColor('#FF0000')
      }
    }

    submitButton.on('pointerdown', () => {
      console.log('🟢 Submit button clicked!')
      submitScore()
    })

    // Allow Enter key to submit
    inputElement.addEventListener('keydown', (event) => {
      console.log('🟢 Key pressed:', event.key)
      if (event.key === 'Enter') {
        console.log('🟢 Enter key detected, submitting...')
        submitScore()
      }
    })
  }

  private async loadAndShowLeaderboard() {
    // Show loading
    this.showLoadingMessage()

    try {
      // Fetch top 5 latest scores
      this.highScores = await leaderboardService.getHighScores(5)

      // Clear and show leaderboard
      this.children.removeAll()
      this.createLeaderboard()
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      // Show leaderboard anyway with whatever we have
      this.children.removeAll()
      this.createLeaderboard()
    }
  }

  private createLeaderboard() {
    const { width, height } = this.scale
    const centerX = width * 0.5

    // Animated background with particles
    const overlay = this.add.rectangle(0, 0, width, height, 0x0a0a1a, 1)
    overlay.setOrigin(0, 0)

    // Decorative top border
    const topBorder = this.add.rectangle(0, 0, width, 10, 0xffd700)
    topBorder.setOrigin(0, 0)

    // BRITRunner LEADERBOARD title
    const title = this.add
      .text(centerX, 80, 'BRITRunner', {
        fontSize: '56px',
        color: '#FFD700',
        fontStyle: 'bold',
        stroke: '#8B4513',
        strokeThickness: 6,
        shadow: { offsetX: 4, offsetY: 4, color: '#000000', blur: 8, fill: true },
      })
      .setOrigin(0.5)

    const subtitle = this.add
      .text(centerX, 140, 'TOP 5 LEADERBOARD', {
        fontSize: '42px',
        color: '#FFFFFF',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5)

    // Animate title
    this.tweens.add({
      targets: title,
      scale: 1.05,
      duration: 1000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    })

    // Trophy decorations
    this.add.text(centerX - 250, 80, '🏆', { fontSize: '48px' }).setOrigin(0.5)
    this.add.text(centerX + 250, 80, '🏆', { fontSize: '48px' }).setOrigin(0.5)

    // Display high scores
    const startY = 200
    const spacing = 45

    if (this.highScores.length === 0) {
      this.add
        .text(centerX, startY + 100, 'No high scores yet!\nBe the first to set a record!', {
          fontSize: '24px',
          color: '#CCCCCC',
          align: 'center',
        })
        .setOrigin(0.5)
    } else {
      this.highScores.forEach((entry, index) => {
        const y = startY + index * spacing
        const rank = index + 1

        // Rank medal/number
        let rankText = `${rank}.`
        let rankColor = '#FFFFFF'
        if (rank === 1) {
          rankText = '🥇'
          rankColor = '#FFD700'
        } else if (rank === 2) {
          rankText = '🥈'
          rankColor = '#C0C0C0'
        } else if (rank === 3) {
          rankText = '🥉'
          rankColor = '#CD7F32'
        }

        // Background for each entry
        const entryBg = this.add.rectangle(centerX, y, width - 100, 40, rank <= 3 ? 0x2a2a3a : 0x1a1a2a, 0.8)
        entryBg.setOrigin(0.5)

        // Rank
        this.add
          .text(centerX - 280, y, rankText, {
            fontSize: '24px',
            color: rankColor,
            fontStyle: 'bold',
          })
          .setOrigin(0.5)

        // Name
        this.add
          .text(centerX - 100, y, entry.name, {
            fontSize: '22px',
            color: rank <= 3 ? '#FFD700' : '#FFFFFF',
            fontStyle: rank <= 3 ? 'bold' : 'normal',
          })
          .setOrigin(0, 0.5)

        // Score
        this.add
          .text(centerX + 180, y, `${entry.score}`, {
            fontSize: '24px',
            color: '#00FF00',
            fontStyle: 'bold',
          })
          .setOrigin(1, 0.5)

        // Fade in animation
        entryBg.setAlpha(0)
        this.tweens.add({
          targets: entryBg,
          alpha: 0.8,
          duration: 300,
          delay: index * 50,
        })
      })
    }

    // Bottom buttons
    const buttonY = height - 80

    const playAgainBtn = this.add
      .text(centerX - 150, buttonY, '[ PLAY AGAIN ]', {
        fontSize: '24px',
        color: '#00FF00',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        padding: { left: 20, right: 20, top: 10, bottom: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })

    const refreshBtn = this.add
      .text(centerX + 150, buttonY, '[ REFRESH SCORES ]', {
        fontSize: '24px',
        color: '#00AAFF',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
        padding: { left: 20, right: 20, top: 10, bottom: 10 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })

    // Button hover effects
    playAgainBtn.on('pointerover', () => {
      playAgainBtn.setScale(1.1)
      playAgainBtn.setColor('#00FFFF')
    })
    playAgainBtn.on('pointerout', () => {
      playAgainBtn.setScale(1)
      playAgainBtn.setColor('#00FF00')
    })

    refreshBtn.on('pointerover', () => {
      refreshBtn.setScale(1.1)
      refreshBtn.setColor('#00DDFF')
    })
    refreshBtn.on('pointerout', () => {
      refreshBtn.setScale(1)
      refreshBtn.setColor('#00AAFF')
    })

    // Button click handlers
    playAgainBtn.on('pointerdown', () => {
      this.scene.stop(SceneKeys.GameOver)
      this.scene.stop(SceneKeys.HighScores)
      this.scene.start(SceneKeys.Game)
    })

    refreshBtn.on('pointerdown', async () => {
      refreshBtn.setText('[ LOADING... ]')
      refreshBtn.disableInteractive()
      await this.loadAndShowLeaderboard()
    })
  }
}
